import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { commonPageMakeStyles } from "../styles/commonPageStyles";
import {
  isSurveyBackgroundLoaded,
  preloadSurveyBackground,
} from "../../helpers/preloadSurveyBackground";

const BACKGROUND_STATUS = {
  NONE: "none", // no background image: plain page, as before
  LOADING: "loading", // image still downloading: no colour yet, so the old background does not flash
  LOADED: "loaded",
  FAILED: "failed", // could not load: plain page, as before
};

function initialStatus(backgroundImage) {
  if (!backgroundImage) return BACKGROUND_STATUS.NONE;
  return isSurveyBackgroundLoaded(backgroundImage)
    ? BACKGROUND_STATUS.LOADED
    : BACKGROUND_STATUS.LOADING;
}

/*
 * SF-4956: the picture sits on its own layer instead of `background-attachment: fixed`, which iOS
 * Safari ignores and then sizes `cover` against the whole (very tall) page, so the picture looks
 * hugely zoomed. Here:
 *  - the clip layer fills this container (behind the questions) and clip-path cuts everything
 *    inside it to that box. Unlike overflow, clip-path also clips position: fixed children, so the
 *    picture never shows over the logo bar or progress bar above.
 *  - the picture layer is position: fixed to the screen, so `cover` fills the screen and the
 *    picture stays still while the questions scroll, on every browser.
 * Only the picture is clipped, so dropdowns in the questions can still open past the edge.
 */
const clipLayerStyle = {
  position: "absolute",
  inset: 0,
  zIndex: -1,
  clipPath: "inset(0)",
  WebkitClipPath: "inset(0)",
  pointerEvents: "none",
};

const pictureLayerBaseStyle = {
  position: "fixed",
  inset: 0,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
};

function BackgroundSecond({ children, backgroundImage }) {
  const commonPageCss = commonPageMakeStyles();
  // SF-4956: survey background picture, set in the survey builder's Settings tab.
  const [status, setStatus] = useState(() => initialStatus(backgroundImage));

  useEffect(() => {
    const next = initialStatus(backgroundImage);
    setStatus(next);
    if (next !== BACKGROUND_STATUS.LOADING) return undefined;

    let active = true;
    preloadSurveyBackground(backgroundImage).then((loaded) => {
      if (active) {
        setStatus(loaded ? BACKGROUND_STATUS.LOADED : BACKGROUND_STATUS.FAILED);
      }
    });
    return () => {
      active = false;
    };
  }, [backgroundImage]);

  const showPicture = status === BACKGROUND_STATUS.LOADED;
  let containerStyle;
  if (showPicture) {
    // position + isolation make this box its own stacking context, so the zIndex -1 picture
    // paints above the page behind it and below the questions.
    containerStyle = {
      backgroundColor: "transparent",
      position: "relative",
      isolation: "isolate",
    };
  } else if (status === BACKGROUND_STATUS.LOADING) {
    containerStyle = { backgroundColor: "transparent" };
  }

  return (
    <Box
      className={`${commonPageCss.BgSecondcontainer} Survey_custom_class`}
      style={containerStyle}
    >
      {showPicture ? (
        <div
          style={clipLayerStyle}
          aria-hidden="true"
          data-testid="survey-background-clip"
        >
          <div
            style={{
              ...pictureLayerBaseStyle,
              backgroundImage: `url("${String(backgroundImage).replace(/"/g, "%22")}")`,
            }}
            data-testid="survey-background-picture"
          />
        </div>
      ) : null}
      <Box className={commonPageCss.BgSecondinnerContainer}>{children}</Box>
    </Box>
  );
}

export default BackgroundSecond;
